import React, { useState } from 'react';
import WithDrawer from '../../../../Dispatch/Common/Drawer/WithDrawer';
import { compose } from 'recompose';
import { getDataWithQueryString } from '../../../../../../api/componentAPIs';
import { withUserProfile } from '../../../../../../userProfile/userProfileContext';
import { withSnackbar } from 'notistack';
import { withStyles, Button, Divider } from '@material-ui/core';
import CustomerDetails from '../../../../Common/AddProToPlan/CustomerDetails';
import ProDetails from '../../../../Common/AddProToPlan/ProDetails';
import ShipperDetails from '../../../../Common/AddProToPlan/ShipperDetails';
import ShipmentDetails from '../../../../Common/AddProToPlan/ShipmentDetails';
import { AppLoading } from '../../../../../../loaders/loaders';
import HoldCustomerDetails from './HoldCustomerDetails';
import useUpdateHoldStatus from '../useUpdateHoldStatus';
import SearchBox from '../../../../Common/AddProToPlan/SearchBox';
import { excludedProStatus } from '../common/Constants';

const styles = (theme) => ({
  addProContainer: {
    height: '100%'
  },
  moduleHeader: {
    color: theme.HeaderSelectedText,
    borderBottom: `1px solid ${theme.AppBGColor}`,
    fontWeight: 'bold',
    padding: `10px 32px`,
    fontSize: theme.headerTextFontSize
  },
  formContainer: {
    padding: 16,
    height: '80%',
    overflowY: 'auto'
  },
  circularProgress: {
    color: theme.white
  },
  actionButton: {
    textTransform: 'none',
    minWidth: 120,
    margin: '0px 4px',
    '&$actionButtonDisabled': {
      backgroundColor: theme.customInputBGColor,
      color: theme.TextColor,
      cursor: 'not-allowed',
      opacity: 0.8
    },
    '&$actionOutlineDisabled': {
      backgroundColor: theme.headerIconColor,
      color: theme.TextColor
    }
  },
  actionButtonDisabled: {},
  actionOutlineDisabled: {},
  actionContainer: {
    display: 'flex',
    paddingTop: 20,
    paddingRight: 20,
    justifyContent: 'flex-end'
  },
  marginNormal: { margin: 0, width: '100%' },
  inputField: {
    padding: 8,
    fontSize: theme.textFontSize,
    color: theme.TextColor,
    height: 'initial'
  },
  button: {
    width: '15%',
    marginLeft: 4,
    height: 34,
    '&$disabledButton,&$disabledButton:hover': {
      cursor: 'not-allowed',
      pointerEvents: 'all',
      backgroundColor: theme.customInputBGColor,
      color: theme.TextColor,
      opacity: 0.8
    }
  },
  searchBoxWrapper: {
    width: '60%',
    display: 'flex'
  },
  searchContainer: {
    display: 'flex',
    marginBottom: 12
  },
  fullInputBox: {
    width: '100%',
    paddingRight: '8px'
  },
  labelClass: {
    lineHeight: '8px',
    color: theme.TextColor,
    transform: 'translate(14px, 14px) scale(1)',
    fontSize: theme.headerTextFontSize,
    opacity: 0.7
  },
  disabledButton: {
    cursor: 'not-allowed',
    pointerEvents: 'all',
    backgroundColor: theme.iconColor,
    color: theme.white
  },
  divider: {
    backgroundColor: theme.AppBGColor
  },
  errorMessage: {
    color: theme.iconColor,
    textAlign: 'center',
    fontSize: theme.textFontSize,
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    height: '75%'
  },
  statusErrorMessage: {
    color: theme.errorBorderColor,
    fontSize: theme.textFontSize,
    display: 'flex',
    alignItems: 'center',
    padding: '2px 0px'
  }
});

const AddToHoldList = ({
  classes,
  enqueueSnackbar,
  tabConfig,
  onClose,
  updateHoldData,
  isExistingData,
  userData,
  tabId
}) => {
  const [searchInputValue, setSearchInputValue] = useState('');
  const [showSearchLoader, setShowSearchLoader] = useState(false);
  const [data, setData] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isAddInProgress, setInProgress] = useState(false);
  const updateHoldStatus = useUpdateHoldStatus();

  const handleSearchChange = (value) => setSearchInputValue(value);

  const onSearch = () => {
    if (!showSearchLoader && searchInputValue) {
      if (isExistingData(searchInputValue)) {
        handleSearchError(`${tabConfig.name} ${searchInputValue} is already in the hold list.`);
        return;
      }
      setData(null);
      setErrorMessage(null);
      setShowSearchLoader(true);
      const params =
        tabId == 0
          ? `proId=${searchInputValue}&planDate=${userData.userProfile.planDate}&terminalId=${userData.userProfile.terminal.id}`
          : `${tabConfig.postApiCode}=${searchInputValue}`;
      getDataWithQueryString(tabConfig.apiRequest, params).then(
        (response) => {
          if (response?.status === 200 && response?.data && Object.keys(response.data).length > 0) {
            setData(response.data);
            setShowSearchLoader(false);
          } else {
            handleSearchError(`No results found for the entered ${tabConfig.name}. Please verify.`);
          }
        },
        () => handleSearchError('An error occurred while fetching Pro details')
      );
    }
  };

  const handleSearchError = (message) => {
    setData(null);
    setShowSearchLoader(false);
    setErrorMessage(message);
  };

  const isAddUpdateButtonDisabled = () => {
    return (
      !data ||
      isAddInProgress ||
      errorMessage ||
      searchInputValue === '' ||
      excludedProStatus.includes(data?.rmaxShipmentStatus)
    );
  };

  const addDataToHold = () => {
    if (isAddUpdateButtonDisabled()) {
      return;
    }

    setInProgress(true);
    updateHoldStatus({
      params: {
        [tabConfig.postApiCode]: [data?.code || data?.proId],
        isOnHold: true,
      },
      methods: {
        onSuccess: () => {
          setInProgress(false);
          onClose(false);
          enqueueSnackbar(
            `${data?.code || data?.proId} ${tabConfig.name} has been added to the hold list.`,
            {
              variant: 'success'
            }
          );
          updateHoldData();
        },
        onError: (response) => {
          setInProgress(false);
          onClose(false);
          if (response?.statusCode === 403) {
            return;
          }
          const errorMessage =
            response?.response?.data?.error?.message ||
            response?.response?.data?.error?.details ||
            response?.message;
          enqueueSnackbar(
            errorMessage || `An error occurred while adding ${tabConfig.name} to the hold list`,
            {
              variant: 'error'
            }
          );
        }
      }
    });
  };

  return (
    <div className={classes.addProContainer}>
      <header className={classes.moduleHeader}>{`Add ${tabConfig.name} to Hold List`}</header>
      <div className={`${classes.formContainer} customScroll`}>
        <SearchBox
          value={searchInputValue}
          onChange={handleSearchChange}
          onSearch={onSearch}
          loading={showSearchLoader}
          disabled={false}
          classes={classes}
          autoFocus
          inputLabel={tabConfig.inputLabel}
          id={tabConfig.postApiCode}
          inputProps={tabConfig.inputProps}
        />
        {showSearchLoader && <AppLoading />}
        {errorMessage && !data && <div className={classes.errorMessage}>{errorMessage}</div>}
        {data && !errorMessage && (
          <>
            {tabConfig.postApiCode === 'proId' ? (
              <>
                {excludedProStatus.includes(data?.rmaxShipmentStatus) && (
                  <div className={classes.statusErrorMessage}>
                    {`${data.rmaxShipmentStatus} shipments cannot be marked as Hold.`}
                  </div>
                )}
                <CustomerDetails proData={data} />
                <Divider className={classes.divider} />
                <ProDetails proData={data} />
                <Divider className={classes.divider} />
                <ShipmentDetails proData={data} />
                <Divider className={classes.divider} />
                <ShipperDetails proData={data} />
              </>
            ) : (
              <HoldCustomerDetails proData={data} />
            )}
          </>
        )}
      </div>
      <div className={classes.actionContainer}>
        <Button
          variant="outlined"
          color="primary"
          size="medium"
          classes={{ root: classes.actionButton }}
          onClick={() => onClose(false)}>
          Close
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="medium"
          type="submit"
          disabled={isAddUpdateButtonDisabled()}
          classes={{ root: classes.actionButton, disabled: classes.actionButtonDisabled }}
          onClick={addDataToHold}>
          {`Add ${tabConfig.name} to Hold List`}
        </Button>
      </div>
    </div>
  );
};

export default compose(
  withStyles(styles),
  withUserProfile,
  withSnackbar,
  WithDrawer
)(AddToHoldList);
